import type { ReactNode } from 'react';
import { useT } from '../i18n/I18nProvider';
import './active-filter-pills.css';

export interface ActiveFilterPill {
  /** Stable key, usually the field name. */
  id: string;
  /** The field, in the operator's words. */
  field: ReactNode;
  /** The value they chose, already resolved to a name. */
  value: ReactNode;
}

/**
 * What is currently narrowing the list, each removable on its own.
 *
 * A count badge on a Filters button says how many filters are on but not which,
 * so an operator looking at a short list cannot tell whether it is short
 * because the data is small or because a filter they forgot is still applied.
 * These pills answer that without opening the drawer.
 */
export function ActiveFilterPills({ pills, onRemove, onClear }: {
  pills: ActiveFilterPill[];
  onRemove: (id: string) => void;
  onClear: () => void;
}) {
  const t = useT();
  if (pills.length === 0) return null;
  return (
    <div className="rr-active-filters" role="group" aria-label={t('filters.active')}>
      {pills.map((p) => (
        <span key={p.id} className="rr-active-filters__pill">
          <span className="rr-active-filters__field">{p.field}:</span>
          <span className="rr-active-filters__value">{p.value}</span>
          <button
            type="button"
            aria-label={t('filters.remove', { field: typeof p.field === 'string' ? p.field : p.id })}
            onClick={() => onRemove(p.id)}
          >
            <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </span>
      ))}
      {pills.length > 1 && (
        <button type="button" className="rr-active-filters__clear" onClick={onClear}>{t('filters.clearAll')}</button>
      )}
    </div>
  );
}
